import React, { useEffect, useMemo, useState } from "react";
import NewsCard from "../components/NewsCard";
import { Search, Loader2, Newspaper } from "lucide-react";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Alle");
  const [hiddenCats, setHiddenCats] = useState([]);
  const [allCats, setAllCats] = useState(["News", "Jubiläum", "Retro", "Glitch", "Community", "Rare", "Event"]);
  const pd = usePageDescription("news", { title: "Alle Habthek-Artikel", intro: "Von Glitches bis Jubiläen – alles über Habbo.de.", keywords: ["Habbo News", "Community", "Events", "Rares"] });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [data, s] = await Promise.all([api.listArticles(), api.getSettings()]);
        setArticles(data);
        setHiddenCats(s.hidden_categories || []);
        if (Array.isArray(s.news_categories) && s.news_categories.length > 0) {
          setAllCats(s.news_categories);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const CATEGORIES = useMemo(() => ["Alle", ...allCats.filter((c) => !hiddenCats.includes(c))], [hiddenCats, allCats]);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      if (hiddenCats.includes(a.category)) return false;
      const matchesCat = cat === "Alle" || a.category === cat;
      const matchesQ = q.trim() === "" ||
        a.title.toLowerCase().includes(q.toLowerCase()) ||
        a.excerpt.toLowerCase().includes(q.toLowerCase());
      return matchesCat && matchesQ;
    });
  }, [q, cat, articles, hiddenCats]);

  return (
    <section className="mx-auto max-w-7xl px-4 py-10">
      <PageHero
        eyebrow={pd.eyebrow || "News-Archiv"}
        icon={Newspaper}
        title={pd.title}
        keywords={pd.keywords}
        intro={pd.intro}
        aside={
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Artikel suchen …"
              className="pl-9 bg-[#10141b] border-white/10 text-zinc-200 placeholder:text-zinc-500"
            />
          </div>
        }
      />

      <div className="mt-6 flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
              cat === c
                ? "border-[#7c4dff]/60 bg-[#7c4dff]/20 text-white"
                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => <NewsCard key={a.id} article={a} />)}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="mt-12 text-center text-sm text-zinc-400">Keine Artikel gefunden.</div>
      )}
    </section>
  );
};

export default News;
